// Work teams (#802) — reusable groups of one business's own staff, managed from the Staff page.
// Admin-rank only, which the server enforces; this screen is reached through the same gate as the
// rest of Staff and never treats a hidden control as authorization.
//
// A team is archived, never deleted. The UI says so out loud rather than offering a Delete that
// would quietly mean something else.
import React from "react";
import { Banner, Button, Dialog, FormField, Input } from "@/ds";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import { useLiveInvalidation } from "@/live/live-provider";
import type { TeamRow, UserRow } from "@/api";
import { useT } from "../i18n";
import type { PushToast } from "@/notifications/toast-queue";
import { Icon } from "@/lib";
import { TeamActionMenu } from "./team-action-menu";
import { staffIdentity, staffInitials } from "./staff-identity";

export interface TeamsSectionProps {
  /** The tenant's staff, already loaded by the Staff screen — the pool a team draws from. */
  staff: UserRow[];
  pushToast: PushToast;
}

export function TeamsSection({ staff, pushToast }: TeamsSectionProps) {
  const t = useT();
  // #1536: the teams from the last visit stay on screen while they refresh.
  const [teams, setTeams] = React.useState<TeamRow[]>(() => cachedRead("listTeams") ?? []);
  const [loading, setLoading] = React.useState(() => cachedRead("listTeams") === undefined);
  const [error, setError] = React.useState<string | null>(null);
  const [openId, setOpenId] = React.useState<string | null>(null);
  const returnTo = React.useRef<HTMLButtonElement | null>(null);
  React.useEffect(() => {
    if (!openId && returnTo.current) document.getElementById(returnTo.current.id)?.focus();
  }, [openId]);

  // Live refetches can overlap; only the newest response may paint.
  const sequence = React.useRef(0);
  const load = React.useCallback(() => {
    setError(null);
    const mine = ++sequence.current;
    return cockpit
      .listTeams()
      .then((next) => {
        if (mine === sequence.current) setTeams(next);
      })
      .catch((e: unknown) => {
        if (mine === sequence.current) setError((e as Error)?.message || t("common.error"));
      })
      .finally(() => {
        if (mine === sequence.current) setLoading(false);
      });
  }, [t]);
  React.useEffect(() => {
    void load();
  }, [load]);

  // Team changes are staff-domain changes on the server (#802), so a second admin editing teams
  // in another browser refreshes this list rather than leaving it quietly wrong.
  const invalidations = useLiveInvalidation("staff");
  React.useEffect(() => {
    if (invalidations === 0) return;
    void load();
  }, [invalidations, load]);

  const open = openId ? (teams.find((team) => team.id === openId) ?? null) : null;

  return (
    <section className="teams-workspace" aria-label={t("teams.title")} data-testid="teams-section">
      {error && (
        <Banner tone="danger" title={t("common.error")}>
          {error}
        </Banner>
      )}

      {open ? (
        <TeamDetail
          team={open}
          staff={staff}
          pushToast={pushToast}
          key={open.id}
          onBack={() => setOpenId(null)}
          onChanged={() => {
            void load();
          }}
        />
      ) : (
        <div className="staff-list">
          <CreateTeam
            heading={<h2>{t(teams.length === 1 ? "teams.count.one" : "teams.count.other", { n: teams.length })}</h2>}
            pushToast={pushToast}
            onCreated={() => {
              void load();
            }}
          />
          {loading && (
            <p className="subtle" style={{ padding: "var(--space-4)" }} aria-busy="true">
              {t("common.loading")}
            </p>
          )}
          <div className="teams-cards">
            {!loading &&
              teams.map((team) => (
                <button
                  type="button"
                  className="teams-card"
                  key={team.id}
                  id={`team-open-${team.id}`}
                  aria-labelledby={`team-open-label-${team.id} team-name-${team.id}`}
                  aria-describedby={`team-meta-${team.id}`}
                  onClick={(event) => { returnTo.current = event.currentTarget; setOpenId(team.id); }}
                >
                  <span className="teams-card__identity">
                    <b id={`team-name-${team.id}`}>{team.name}</b>
                    <span className="teams-card__meta" id={`team-meta-${team.id}`}>
                      {t("teams.memberCount", { n: team.members.length })}
                      {team.archivedAt && <span className="rolechip">{t("teams.archived")}</span>}
                    </span>
                  </span>
                  <span className="teams-card__open">
                    <span id={`team-open-label-${team.id}`}>{t("teams.open")}</span>
                    <Icon name="chevronRight" size={16} />
                  </span>
                </button>
              ))}
          </div>
          {!loading && !teams.length && (
            <p className="subtle" style={{ padding: "var(--space-4)" }}>
              {t("teams.none")}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function CreateTeam({
  heading,
  pushToast,
  onCreated,
}: {
  heading: React.ReactNode;
  pushToast: PushToast;
  onCreated: () => void;
}) {
  const t = useT();
  const [name, setName] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const createAction = React.useRef<HTMLButtonElement | null>(null);
  const returnFocusToCreate = React.useRef(false);
  React.useLayoutEffect(() => {
    if (creating || !returnFocusToCreate.current) return;
    returnFocusToCreate.current = false;
    createAction.current?.focus();
  }, [creating]);
  const close = () => {
    // Frame callbacks can run before React unhides the header action under load.
    returnFocusToCreate.current = true;
    setCreating(false);
    setName("");
    setError(null);
  };
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const submit = () => {
    const clean = name.trim();
    if (!clean || busy) return;
    setBusy(true);
    setError(null);
    cockpit
      .createTeam(clean)
      .then(() => {
        pushToast({ tone: "success", title: t("teams.created") });
        close();
        onCreated();
      })
      .catch((e: unknown) => setError((e as Error)?.message || t("common.error")))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <div className="teams-workspace__head staff-list__head">
        {heading}
        <Button hidden={creating} variant="primary" iconLeft={<Icon name="plus" size={16} />}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => { createAction.current = event.currentTarget; setCreating(true); }}>
          {t("teams.create")}
        </Button>
      </div>
      {creating && <div className="team-create stack-sm">
        {error && (
          <Banner tone="danger" title={t("common.error")}>
            {error}
          </Banner>
        )}
        <div className="management-field-action">
          <FormField label={t("teams.namePlaceholder")}>
            <Input
              type="text"
              autoFocus
              value={name}
              maxLength={80}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </FormField>
          <div className="management-actions">
            <Button aria-label={t("teams.create")} onClick={submit} loading={busy} disabled={!name.trim()}>
              {t("teams.create")}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={close}>{t("common.cancel")}</Button>
          </div>
        </div>
      </div>}
    </>
  );
}

function TeamDetail({ team, staff, pushToast, onBack, onChanged }: {
  team: TeamRow;
  staff: UserRow[];
  pushToast: PushToast;
  onBack: () => void;
  onChanged: () => void;
}) {
  const t = useT();
  const [name, setName] = React.useState(team.name);
  const [editing, setEditing] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [confirmArchive, setConfirmArchive] = React.useState(false);
  const [pick, setPick] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const busyRef = React.useRef(false);
  const [error, setError] = React.useState<string | null>(null);
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  const renameRef = React.useRef<HTMLButtonElement | null>(null);
  const addRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLButtonElement | null>(null);
  const archived = Boolean(team.archivedAt);

  React.useEffect(() => { titleRef.current?.focus({ preventScroll: true }); }, []);
  React.useEffect(() => { setName(team.name); }, [team.id, team.name]);
  // A live archive closes editable surfaces as soon as the canonical state changes.
  React.useEffect(() => {
    if (archived) { setEditing(false); setAdding(false); setConfirmArchive(false); }
  }, [archived]);

  const run = async (op: () => Promise<unknown>, title: string, after?: () => void) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await op();
      pushToast({ tone: "success", title });
      onChanged();
      after?.();
    } catch (e) {
      setError((e as Error)?.message || t("common.error"));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const cancelRename = () => {
    setEditing(false);
    setName(team.name);
    setError(null);
    if (typeof window !== "undefined") window.requestAnimationFrame(() => renameRef.current?.focus());
  };
  const closeAdd = () => { setAdding(false); setError(null); };
  const inTeam = new Set(team.members.map(member => member.id));
  const addable = staff.filter(person => !inTeam.has(person.id));
  const query = search.trim().toLocaleLowerCase();
  const matches = addable.filter(person => `${person.name ?? ""} ${person.email}`.toLocaleLowerCase().includes(query));
  const picked = addable.find(person => person.id === pick);
  const banner = error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>;

  return (
    <div className="team-detail">
      <Button variant="ghost" size="sm" className="team-detail__back" iconLeft={<Icon name="arrowLeft" size={16} />}
        disabled={busy} onClick={onBack}>{t("teams.back")}</Button>
      <div className="team-detail__head">
        <h2 tabIndex={-1} ref={titleRef}>{team.name}</h2>
        <div className="team-detail__tools">
          {!archived && <Button variant="ghost" size="sm" hidden={editing} disabled={busy}
            iconLeft={<Icon name="pencil" size={16} />}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              renameRef.current = event.currentTarget;
              setError(null); setName(team.name); setEditing(true);
            }}>{t("teams.rename")}</Button>}
          <TeamActionMenu label={t("teams.actions")} disabled={busy} triggerRef={menuRef}
            items={[archived ? {
              label: t("teams.restore"),
              onClick: () => { void run(() => cockpit.restoreTeam(team.id, team.version), t("teams.restored")); },
            } : {
              label: t("teams.archive"),
              onClick: () => { setError(null); setConfirmArchive(true); },
            }]} />
        </div>
      </div>
      {archived && <Banner tone="warning" title={t("teams.archived")}>{t("teams.archivedNotice")}</Banner>}
      {!adding && !confirmArchive && banner}
      {editing && !archived && <form className="team-rename stack-sm" data-testid="team-rename"
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim() || name.trim() === team.name) return;
          void run(() => cockpit.renameTeam(team.id, name.trim(), team.version), t("teams.renamed"), cancelRename);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !busy) { event.preventDefault(); cancelRename(); }
        }}>
        <FormField label={t("teams.namePlaceholder")}>
          <Input value={name} maxLength={80} autoFocus disabled={busy}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)} />
        </FormField>
        <div className="management-actions">
          <Button type="submit" aria-label={t("teams.saveName")} loading={busy} disabled={!name.trim() || name.trim() === team.name}>{t("teams.saveName")}</Button>
          <Button variant="ghost" disabled={busy} onClick={cancelRename}>{t("common.cancel")}</Button>
        </div>
      </form>}
      <section className="team-detail__members" aria-labelledby={`team-members-${team.id}`}>
        <div className="team-detail__members-head">
          <h3 id={`team-members-${team.id}`} tabIndex={-1}>{t("teams.members")} <span className="subtle">· {team.members.length}</span></h3>
          {!archived && <Button iconLeft={<Icon name="plus" size={16} />} disabled={busy}
            aria-haspopup="dialog" onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              addRef.current = event.currentTarget;
              setError(null); setPick(""); setSearch(""); setAdding(true);
            }}>{t("teams.addMemberTitle")}</Button>}
        </div>
        <div className="team-detail__roster">
          {!team.members.length && <p className="team-detail__empty subtle">{t("teams.noMembers")}</p>}
          {team.members.map(member => {
            const identity = staffIdentity(member);
            return <div className="team-row" key={member.id}>
              <span className="team-row__avatar" aria-hidden="true">{staffInitials(member)}</span>
              <span className="team-row__id">
                <b>{identity.primary}</b>
                {identity.secondary && <span className="team-row__meta subtle">{identity.secondary}</span>}
              </span>
              <TeamActionMenu label={t("teams.memberActions", { name: identity.primary })} disabled={busy}
                items={[{ label: t("teams.removeFromTeam"), onClick: () => {
                  void run(() => cockpit.removeTeamMember(team.id, member.id), t("teams.memberRemoved"), () => {
                    document.getElementById(`team-members-${team.id}`)?.focus();
                  });
                } }]} />
            </div>;
          })}
        </div>
      </section>

      <Dialog open={adding} onClose={busy ? undefined : closeAdd} closeButton={false}
        title={t("teams.addMemberTitle")} subtitle={team.name} finalFocusRef={addRef} className="team-member-dialog"
        footer={<div className="management-actions management-actions--end">
          <Button variant="ghost" disabled={busy} onClick={closeAdd}>{t("common.cancel")}</Button>
          <Button aria-label={t("teams.addMember")} loading={busy} disabled={!picked} onClick={() => {
            if (picked) void run(() => cockpit.addTeamMember(team.id, picked.id), t("teams.memberAdded"), () => setAdding(false));
          }}>{t("teams.addMember")}</Button>
        </div>}>
        <div className="stack-sm" data-testid="team-add-member">
          {banner}
          <FormField label={t("teams.searchPeople")}>
            <Input type="search" value={search} disabled={busy}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)} />
          </FormField>
          <div className="team-person-options" role="group" aria-label={t("teams.choosePerson")}>
            {matches.map(person => {
              const identity = staffIdentity(person);
              return <label className="team-person-option" key={person.id}>
                <input type="radio" name={`team-person-${team.id}`} value={person.id} checked={pick === person.id}
                  disabled={busy} onChange={() => setPick(person.id)} />
                <span className="team-row__avatar" aria-hidden="true">{staffInitials(person)}</span>
                <span className="team-row__id"><b>{identity.primary}</b>
                  {identity.secondary && <span className="team-row__meta subtle">{identity.secondary}</span>}
                </span>
              </label>;
            })}
            {!matches.length && <p className="subtle" role="status">{t(addable.length ? "teams.searchEmpty" : "teams.allAdded")}</p>}
          </div>
          {picked && <p className="team-person-selection subtle" role="status">{t("teams.selectedPerson", { name: staffIdentity(picked).primary })}</p>}
        </div>
      </Dialog>
      <Dialog open={confirmArchive} onClose={busy ? undefined : () => { setConfirmArchive(false); setError(null); }}
        closeButton={false} tone="warning" title={t("teams.archiveConfirm", { name: team.name })} finalFocusRef={menuRef}
        footer={<div className="management-actions management-actions--end">
          <Button variant="ghost" disabled={busy} onClick={() => { setConfirmArchive(false); setError(null); }}>{t("common.cancel")}</Button>
          <Button variant="destructive" aria-label={t("teams.archive")} loading={busy} onClick={() => {
            void run(() => cockpit.archiveTeam(team.id, team.version), t("teams.archiveDone"), () => setConfirmArchive(false));
          }}>{t("teams.archive")}</Button>
        </div>}>
        <div className="stack-sm">{banner}<p>{t("teams.archiveHint")}</p></div>
      </Dialog>
    </div>
  );
}
